/**
 * Building blocks for list / details selection motion: the outgoing panel is cloned into a fixed
 * "ghost" that fades away in place while the live panel fades in, and a block can "fold" between
 * two sizes (a hero card shrinking into a one-line strip and back) as a single object travelling
 * across the page.
 *
 * Everything is Web Animations API based and degrades to "no animation" where `Element.animate`
 * is missing (jsdom), so callers never need to guard.
 */

export const FADE_OUT_MS = 150;
export const FADE_IN_MS = 180;
export const FOLD_MS = 380;
export const EASE_OUT = "cubic-bezier(.23,1,.32,1)";
/** An even in-out, so the fold's whole trip is seen and not just its two ends. */
export const EASE_FOLD = "cubic-bezier(.45,0,.15,1)";

/** Above the page, below dialogs and overlays. */
const GHOST_Z_INDEX = "30";

export interface MotionRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function rectOf(el: Element): MotionRect {
  const r = el.getBoundingClientRect();
  return { x: r.left, y: r.top, w: r.width, h: r.height };
}

export function canAnimate(): boolean {
  return typeof Element !== "undefined" && typeof Element.prototype.animate === "function";
}

/** A clone must not repeat the ids of the live DOM it was copied from. */
export function stripIds<T extends Element>(node: T): T {
  node.removeAttribute("id");
  node.querySelectorAll("[id]").forEach((child) => child.removeAttribute("id"));
  return node;
}

/** A fixed, non-interactive copy of `el` sitting exactly where `el` is now. */
export function createGhost(el: HTMLElement): { ghost: HTMLElement; rect: MotionRect } {
  const rect = rectOf(el);
  const ghost = stripIds(el.cloneNode(true) as HTMLElement);
  Object.assign(ghost.style, {
    position: "fixed",
    left: `${rect.x}px`,
    top: `${rect.y}px`,
    width: `${rect.w}px`,
    height: `${rect.h}px`,
    margin: "0",
    zIndex: GHOST_Z_INDEX,
    pointerEvents: "none",
    willChange: "opacity",
    overflow: "hidden",
  });
  ghost.setAttribute("aria-hidden", "true");
  ghost.removeAttribute("data-testid");
  ghost.querySelectorAll("[data-testid]").forEach((n) => n.removeAttribute("data-testid"));
  document.body.appendChild(ghost);
  // A panel scrolled down keeps showing what was in view: the clone starts at the top otherwise.
  ghost.scrollTop = el.scrollTop;
  return { ghost, rect };
}

export function fadeIn(el: Element, delay = 0, duration = FADE_IN_MS): void {
  if (!canAnimate()) {
    return;
  }
  el.animate(
    [
      { opacity: 0, transform: "translateY(6px)" },
      { opacity: 1, transform: "none" },
    ],
    { duration, delay, easing: EASE_OUT, fill: "backwards" },
  );
}

/** Fades a ghost out in place and removes it. Resolves once it is gone. */
export function fadeOutAndRemove(ghost: HTMLElement, duration = FADE_OUT_MS): Promise<void> {
  if (!canAnimate()) {
    ghost.remove();
    return Promise.resolve();
  }
  return ghost
    .animate([{ opacity: 1 }, { opacity: 0 }], { duration, easing: EASE_OUT, fill: "forwards" })
    .finished.catch(() => undefined)
    .then(() => ghost.remove());
}

let activeFlight: (() => void) | null = null;

/** Lands whatever fold is in the air right now, so a new selection never fights a running one. */
export function endFlight(): void {
  const end = activeFlight;
  activeFlight = null;
  end?.();
}

export interface SurfaceStyle {
  backgroundColor: string;
  boxShadow: string;
}

export function surfaceStyleOf(el: Element): SurfaceStyle {
  const style = getComputedStyle(el);
  return { backgroundColor: style.backgroundColor, boxShadow: style.boxShadow };
}

export interface FoldOptions {
  /** Clone of the face that leaves, sized `from`. */
  leaving: HTMLElement;
  from: MotionRect;
  /** Clone of the face that arrives, sized `to`. */
  arriving: HTMLElement;
  to: MotionRect;
  /** Chrome (background + shadow) of the hero-sized card and of the strip-sized one. */
  hero: SurfaceStyle;
  strip: SurfaceStyle;
  /** "down": hero folds into the strip. "up": the strip opens back into the hero. */
  direction: "down" | "up";
  /** The live element hidden until the ghost lands, then shown in the same frame. */
  reveal?: HTMLElement;
}

function layer(className: string, cssText: string): HTMLElement {
  const div = document.createElement("div");
  div.className = className;
  div.style.cssText = `position:absolute;left:0;top:0;border-radius:20px;${cssText}`;
  return div;
}

/**
 * One box travels (transform only) while a face inside it resizes between the two sizes and the two
 * contents cross-fade: the box is never blank mid-trip. The hero's lift (its extra shadow) fades off
 * as it becomes the flat bar and back on as it rises. The caller animates everything else.
 */
export function fold(options: FoldOptions): Promise<void> {
  endFlight();
  const { leaving, arriving, from, to, hero, strip, direction, reveal } = options;

  if (!canAnimate()) {
    return Promise.resolve();
  }

  const box = document.createElement("div");
  box.setAttribute("aria-hidden", "true");
  box.style.cssText = [
    "position:fixed",
    `z-index:${GHOST_Z_INDEX}`,
    "pointer-events:none",
    "transform-origin:0 0",
    "will-change:transform,opacity",
    `left:${from.x}px`,
    `top:${from.y}px`,
    `width:${from.w}px`,
    `height:${from.h}px`,
  ].join(";");

  const heroLift = layer("fold-hero-lift", `box-shadow:${hero.boxShadow}`);
  const stripLift = layer("fold-strip-lift", `box-shadow:${strip.boxShadow}`);
  const face = layer("fold-face", `overflow:hidden;background:${strip.backgroundColor}`);
  const under = document.createElement("div");
  under.style.cssText = `position:absolute;inset:0;background:${hero.backgroundColor}`;

  for (const [node, rect] of [
    [leaving, from],
    [arriving, to],
  ] as const) {
    stripIds(node);
    Object.assign(node.style, {
      position: "absolute",
      left: "0",
      top: "0",
      width: `${rect.w}px`,
      height: `${rect.h}px`,
      margin: "0",
      background: "transparent",
      boxShadow: "none",
      transition: "none",
      pointerEvents: "none",
    });
    node.removeAttribute("data-testid");
    node.querySelectorAll("[data-testid]").forEach((n) => n.removeAttribute("data-testid"));
  }
  arriving.style.opacity = "0";

  face.append(under, leaving, arriving);
  box.append(stripLift, heroLift, face);
  document.body.appendChild(box);

  const size = [
    { width: `${from.w}px`, height: `${from.h}px` },
    { width: `${to.w}px`, height: `${to.h}px` },
  ];
  const timing: KeyframeAnimationOptions = {
    duration: FOLD_MS,
    easing: EASE_FOLD,
    fill: "forwards",
  };

  const travel = box.animate(
    [
      { transform: "translate(0,0)" },
      { transform: `translate(${to.x - from.x}px,${to.y - from.y}px)` },
    ],
    timing,
  );
  face.animate(size, timing);
  heroLift.animate(size, timing);
  stripLift.animate(size, timing);

  // The two faces overlap mid-trip so the box is never empty.
  leaving.animate([{ opacity: 1 }, { opacity: 0 }], {
    duration: FOLD_MS * 0.4,
    delay: FOLD_MS * 0.15,
    easing: "ease-in-out",
    fill: "both",
  });
  arriving.animate([{ opacity: 0 }, { opacity: 1 }], {
    duration: FOLD_MS * 0.4,
    delay: FOLD_MS * 0.35,
    easing: "ease-in-out",
    fill: "both",
  });

  // The lift goes last as the bar lands flat, and comes first as the hero rises.
  const liftOut: Keyframe[] = [{ opacity: 1 }, { opacity: 0 }];
  const liftIn: Keyframe[] = [{ opacity: 0 }, { opacity: 1 }];
  const down = direction === "down";
  const liftTiming: KeyframeAnimationOptions = down
    ? { duration: FOLD_MS * 0.3, delay: FOLD_MS * 0.7, easing: "ease-out", fill: "both" }
    : { duration: FOLD_MS * 0.3, easing: "ease-out", fill: "both" };
  under.animate(down ? liftOut : liftIn, liftTiming);
  heroLift.animate(down ? liftOut : liftIn, liftTiming);
  stripLift.animate(down ? liftIn : liftOut, liftTiming);

  if (reveal) {
    reveal.style.opacity = "0";
  }

  let done = false;
  const end = () => {
    if (done) {
      return;
    }
    done = true;
    // The live element shows in the same frame the ghost goes.
    if (reveal) {
      reveal.style.opacity = "";
    }
    box.remove();
    if (activeFlight === end) {
      activeFlight = null;
    }
  };
  activeFlight = end;

  return travel.finished.catch(() => undefined).then(end);
}
